import { useCallback, useMemo, useState } from 'react';
import type { ActionMeta, MultiValue } from 'react-select';
import { useDebouncedValue } from '@/hooks/useDebounceValue';
import { MultiSelect } from '../../MultiSelect';
import type { MultiSelectProps, OptionType } from '../../MultiSelect/types';
import { useFetchInfinityOptions } from '../hooks/useFetchInfinityOptions';
import { triggerBlur } from '../utils/triggerBlur';

interface MultiSelectWithInfiniteScrollProps<T> extends Omit<MultiSelectProps<OptionType>, 'onChange' | 'options'> {
  queryKey: string[];
  onChange: (value: MultiValue<OptionType>, actionMeta: ActionMeta<OptionType>) => void;
  url: string;
  optionType?: 'check-box-right' | 'default';
  mapOptions: (options: T[]) => OptionType[];
  staleTime?: number;
  gcTime?: number;
}

export const MultiSelectWitInfiniteScroll = <T,>(props: MultiSelectWithInfiniteScrollProps<T>) => {
  const { name, onChange, mapOptions, ...rest } = props;

  const handleChange = useCallback(
    (value: MultiValue<OptionType>, actionMeta: ActionMeta<OptionType>) => {
      onChange?.(value, actionMeta);
      if (name) {
        triggerBlur(name);
      }
    },
    [onChange, name]
  );

  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebouncedValue(searchValue);

  const {
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    data: rawOptions,
  } = useFetchInfinityOptions({
    queryKey: props.queryKey,
    searchValue: debouncedSearchValue,
    url: props.url,
    staleTime: props.staleTime,
    gcTime: props.gcTime,
  });

  const options = useMemo(() => mapOptions(rawOptions as T[]), [rawOptions, mapOptions]);

  const onMenuScrollToBottom = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const onInputChange = (val: string) => {
    setSearchValue?.(val);
  };

  return (
    <>
      <MultiSelect
        {...rest}
        options={options}
        menuPosition="fixed"
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        optionType={props.optionType}
        isClearable={false}
        fetchNextPage={fetchNextPage}
        onMenuScrollToBottom={onMenuScrollToBottom}
        onInputChange={onInputChange}
        onChange={handleChange as (newValue: MultiValue<OptionType>, actionMeta: ActionMeta<OptionType>) => void}
      />
    </>
  );
};